// Y.UndoManager auf die "objects"-Y.Map gescopt.
// Trackt nur Mutationen ohne Origin (== lokale Aktionen); Remote-Updates haben
// origin = WebsocketProvider und werden somit nicht zur Undo-History.

import { useEffect, useState } from "react";
import * as Y from "yjs";

export interface UndoApi {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

// biome-ignore lint/suspicious/noExplicitAny: Y.UndoManager braucht den weiten AbstractType<any>-Konstruktor von Yjs
export function useUndoManager(yMap: Y.AbstractType<any> | null): UndoApi {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [manager, setManager] = useState<Y.UndoManager | null>(null);

  useEffect(() => {
    if (!yMap) {
      setManager(null);
      return;
    }
    const um = new Y.UndoManager(yMap, {
      captureTimeout: 400,
    });
    setManager(um);
    const refresh = (): void => {
      setCanUndo(um.canUndo());
      setCanRedo(um.canRedo());
    };
    um.on("stack-item-added", refresh);
    um.on("stack-item-popped", refresh);
    um.on("stack-cleared", refresh);
    refresh();
    return () => {
      um.destroy();
      setManager(null);
    };
  }, [yMap]);

  // Tastatur-Shortcuts Strg/Cmd-Z + Strg/Cmd-Shift-Z.
  useEffect(() => {
    if (!manager) return;
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) manager.redo();
        else manager.undo();
      } else if (e.key.toLowerCase() === "y") {
        e.preventDefault();
        manager.redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [manager]);

  return {
    canUndo,
    canRedo,
    undo: () => manager?.undo(),
    redo: () => manager?.redo(),
  };
}
